import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthHeaders, isAuthed } from '../utils/auth';
import './FinalizePage.css';

const formatDateTime = (input) => {
  if (!input) return '-';
  const safe = String(input).replace(' ', 'T');
  const parsed = new Date(safe);
  if (Number.isNaN(parsed.getTime())) return String(input);
  return parsed.toLocaleString();
};

/**
 * FinalizePage
 * Fluxo guiado para: Remove -> Ajuste (validação) -> Invoice -> Shipment -> Complete
 * Requer: token no localStorage; PickingPage deve ter marcado itens (found/removed) e, opcionalmente, editedQty.
 */
const FinalizePage = () => {
  const { incrementId: rawId } = useParams(); // order increment_id (e.g. "000000005-1")
  const incrementId = rawId; // usar valor completo como está
  const navigate = useNavigate();

  // Estado base
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyMsg, setApplyMsg] = useState('');
  const [hasApplied, setHasApplied] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState('');
  const [shipmentBusy, setShipmentBusy] = useState(false);
  const [shipmentMsg, setShipmentMsg] = useState('');
  const [forceCompleteReady, setForceCompleteReady] = useState(false);
  const [forceCompleteBusy, setForceCompleteBusy] = useState(false);
  const [forceCompleteMsg, setForceCompleteMsg] = useState('');
  const operatorName = React.useMemo(() => {
    const raw =
      localStorage.getItem('operator') ||
      localStorage.getItem('username') ||
      localStorage.getItem('displayName') ||
      '';
    const trimmed = raw ? String(raw).trim() : '';
    if (trimmed) return trimmed;
    const fallbackRole = localStorage.getItem('roleName') || '';
    return fallbackRole.trim();
  }, []);
  const operatorLabel = operatorName || 'Unknown Picker';

  const rawRestBase = ((localStorage.getItem('restBase') || '/rest/V1').trim() || '/rest/V1');
  const restBase = rawRestBase.replace(/\/+$/, '');

  const buildRestUrl = React.useCallback((path = '') => {
    const cleaned = String(path).trim().replace(/^\/+/, '');
    if (!cleaned) return restBase;
    return `${restBase}/${cleaned}`;
  }, [restBase]);

  const toKey = (value) => {
    if (value === undefined || value === null) return undefined;
    return String(value);
  };

  const normalizeEditEntry = (entry, maxQty) => {
    const safeMax = Number.isFinite(maxQty) && maxQty > 0 ? maxQty : 0;
    const clamp = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return 0;
      if (num < 0) return 0;
      if (num > safeMax) return safeMax;
      return num;
    };

    if (entry && typeof entry === 'object') {
      if (entry.removedQty !== undefined || entry.removed !== undefined || entry.diff !== undefined) {
        const removed = clamp(entry.removedQty ?? entry.removed ?? entry.diff);
        return { keepQty: Math.max(safeMax - removed, 0), removedQty: removed };
      }
      if (entry.keepQty !== undefined || entry.keep !== undefined || entry.qty !== undefined) {
        const keep = clamp(entry.keepQty ?? entry.keep ?? entry.qty);
        return { keepQty: keep, removedQty: Math.max(safeMax - keep, 0) };
      }
    }

    if (entry !== undefined) {
      const keep = clamp(entry);
      return { keepQty: keep, removedQty: Math.max(safeMax - keep, 0) };
    }

    return null;
  };

  const getEditedEntryForItem = (item, maxQty) => {
    const safeMax = Number.isFinite(maxQty) && maxQty > 0 ? maxQty : 0;
    const itemKey = toKey(item.item_id);
    if (itemKey && Object.prototype.hasOwnProperty.call(editedQty, itemKey)) {
      const direct = normalizeEditEntry(editedQty[itemKey], safeMax);
      if (direct) return direct;
    }
    if (item.parent_item_id) {
      const parentKey = toKey(item.parent_item_id);
      if (parentKey && Object.prototype.hasOwnProperty.call(editedQty, parentKey)) {
        const parentEntry = normalizeEditEntry(editedQty[parentKey], safeMax);
        if (parentEntry) return parentEntry;
      }
    }
    return null;
  };

  // Carrega ordem por increment_id
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const url = buildRestUrl(`orders?searchCriteria[filter_groups][0][filters][0][field]=increment_id&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(incrementId)}&searchCriteria[filter_groups][0][filters][0][condition_type]=eq`);
        const res = await fetch(url, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const item = Array.isArray(data?.items) ? data.items[0] : null;
        if (!item) throw new Error(`Order not found for increment_id ${incrementId}`);
        if (isMounted) setOrder(item);
      } catch (e) {
        console.error(e);
        if (isMounted) setError(e.message || 'Failed to load order');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [incrementId, buildRestUrl]);

  const reloadOrder = async () => {
    try {
      setLoading(true);
      const url = buildRestUrl(`orders?searchCriteria[filter_groups][0][filters][0][field]=increment_id&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(incrementId)}&searchCriteria[filter_groups][0][filters][0][condition_type]=eq`);
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const item = Array.isArray(data?.items) ? data.items[0] : null;
      if (!item) throw new Error(`Order not found for increment_id ${incrementId}`);
      setOrder(item);
      setError('');
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const lastOrderIdRef = useRef(null);
  useEffect(() => {
    const currentId = order?.entity_id || null;
    if (currentId && currentId !== lastOrderIdRef.current) {
      setHasApplied(false);
      setApplyMsg('');
      setInvoiceMsg('');
      setShipmentMsg('');
      lastOrderIdRef.current = currentId;
      setForceCompleteReady(false);
      setForceCompleteMsg('');
      setForceCompleteBusy(false);
    }
    if (!currentId) {
      setHasApplied(false);
      setApplyMsg('');
      setInvoiceMsg('');
      setShipmentMsg('');
      lastOrderIdRef.current = null;
      setForceCompleteReady(false);
      setForceCompleteMsg('');
      setForceCompleteBusy(false);
    }
  }, [order?.entity_id]);

  // Snapshots salvos pelo PickingPage
  const [pickedStore] = useState(() => {
    try {
      const v1 = localStorage.getItem('pickedSelections');
      if (v1) return JSON.parse(v1);
      const v0 = localStorage.getItem('pickedItems');
      return v0 ? JSON.parse(v0) : {};
    } catch {
      return {};
    }
  });

  const [editedStore, setEditedStore] = useState(() => {
    try {
      const v1 = localStorage.getItem('editedQtyByOrder');
      if (v1) return JSON.parse(v1);
      const v0 = localStorage.getItem('editedQty');
      return v0 ? JSON.parse(v0) : {};
    } catch {
      return {};
    }
  });

  const clearEditedEntriesForOrder = React.useCallback(() => {
    if (!order) return;
    const candidates = [
      order.increment_id,
      order.entity_id,
      incrementId,
    ].map((val) => (val === undefined || val === null ? undefined : String(val))).filter(Boolean);

    if (!candidates.length) return;

    setEditedStore((prev) => {
      let changed = false;
      const next = { ...prev };
      candidates.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(next, key)) {
          delete next[key];
          changed = true;
        }
      });
      if (!changed) return prev;
      try {
        localStorage.setItem('editedQtyByOrder', JSON.stringify(next));
        localStorage.removeItem('editedQty');
      } catch (err) {
        console.warn('Failed to persist editedQty cleanup:', err);
      }
      return next;
    });

    try {
      const raw = localStorage.getItem('revealedLocationsByOrder');
      if (raw) {
        const parsed = JSON.parse(raw);
        let mutated = false;
        candidates.forEach((key) => {
          if (key && Object.prototype.hasOwnProperty.call(parsed, key)) {
            delete parsed[key];
            mutated = true;
          }
        });
        if (mutated) {
          const hasKeys = Object.keys(parsed).length > 0;
          if (hasKeys) {
            localStorage.setItem('revealedLocationsByOrder', JSON.stringify(parsed));
          } else {
            localStorage.removeItem('revealedLocationsByOrder');
          }
        }
      }
    } catch (err) {
      console.warn('Failed to cleanup revealed locations:', err);
    }
  }, [order, incrementId]);

  // Helper para achar o mapa da ordem com várias possíveis chaves (increment_id/entity_id, string/number)
  const mapForOrder = (storeObj) => {
    if (!order || !storeObj) return {};

    const candidates = [
      String(order.increment_id),
      String(order.entity_id),
      String(incrementId),
    ].filter(Boolean);
    for (const k of candidates) {
      if (Object.prototype.hasOwnProperty.call(storeObj, k)) return storeObj[k] || {};
    }

    const itemIds = new Set((order.items || []).map((i) => String(i.item_id)));
    for (const [key, val] of Object.entries(storeObj)) {
      if (val && typeof val === 'object') {
        const keys = Object.keys(val);
        const hit = keys.some((id) => itemIds.has(String(id)));
        if (hit) return val;
      }
    }

    return {};
  };

  const pickedMap = useMemo(() => mapForOrder(pickedStore), [order, pickedStore, incrementId]);
  const editedQty = useMemo(() => mapForOrder(editedStore), [order, editedStore, incrementId]);

  const normStatus = (status) => {
    const s = typeof status === 'string' ? status.toLowerCase() : status;
    return s;
  };
  const isFoundStatus = (status) => {
    const s = normStatus(status);
    return s === 'found' || s === true || s === 'f' || s === 'ok';
  };
  const isRemovedStatus = (status) => {
    const s = normStatus(status);
    return s === 'removed' || s === 'remove' || s === false || s === 'rm';
  };
  const isHoldStatus = (status) => {
    const s = normStatus(status);
    return s === 'hold' || s === 'on_hold';
  };
  const isRemovedForItem = (it) => {
    const sSelf = pickedMap[String(it.item_id)];
    if (isRemovedStatus(sSelf)) return true;
    if (it.parent_item_id) {
      const sParent = pickedMap[String(it.parent_item_id)];
      if (isRemovedStatus(sParent)) return true;
    }
    return false;
  };
  const isFoundForItem = (it) => {
    const sSelf = pickedMap[String(it.item_id)];
    if (isFoundStatus(sSelf)) return true;
    if (it.parent_item_id) {
      const sParent = pickedMap[String(it.parent_item_id)];
      if (isFoundStatus(sParent)) return true;
    }
    return false;
  };
  const isHoldForItem = (it) => {
    const sSelf = pickedMap[String(it.item_id)];
    if (isHoldStatus(sSelf)) return true;
    if (it.parent_item_id) {
      const sParent = pickedMap[String(it.parent_item_id)];
      if (isHoldStatus(sParent)) return true;
    }
    return false;
  };

  const editedQtyForItem = (it, maxInv) => {
    const safeMax = Number.isFinite(maxInv) && maxInv > 0 ? maxInv : 0;
    const entry = getEditedEntryForItem(it, safeMax);
    if (!entry) return safeMax;
    const keep = Number(entry.keepQty);
    if (!Number.isFinite(keep)) return safeMax;
    if (keep < 0) return 0;
    if (keep > safeMax) return safeMax;
    return keep;
  };

  const removedQtyForItem = (it, maxInv) => {
    const safeMax = Number.isFinite(maxInv) && maxInv > 0 ? maxInv : 0;
    const entry = getEditedEntryForItem(it, safeMax);
    if (!entry) return 0;
    const removed = Number(entry.removedQty);
    if (!Number.isFinite(removed)) return 0;
    if (removed < 0) return 0;
    if (removed > safeMax) return safeMax;
    return removed;
  };

  const foundItems = useMemo(() => {
    if (!order) return [];
    return (order.items || []).filter((it) => {
      const status = pickedMap[String(it.item_id)];
      return isFoundStatus(status);
    });
  }, [order, pickedMap]);

  const removedItems = useMemo(() => {
    if (!order) return [];
    return (order.items || []).filter((it) => {
      const status = pickedMap[String(it.item_id)];
      return isRemovedStatus(status);
    });
  }, [order, pickedMap]);

  const allResolved = useMemo(() => {
    if (!order) return false;
    const items = order.items || [];
    if (!items.length) return false;
    return items.every((it) => {
      const status = pickedMap[String(it.item_id)];
      return isFoundStatus(status) || isRemovedStatus(status);
    });
  }, [order, pickedMap]);

  const pendingCount = useMemo(() => {
    if (!order) return 0;
    const items = order.items || [];
    return items.reduce((acc, it) => {
      const status = pickedMap[String(it.item_id)];
      return acc + (isFoundStatus(status) || isRemovedStatus(status) ? 0 : 1);
    }, 0);
  }, [order, pickedMap]);

  const qtyToInvoice = (it) => {
    const direct = Number(it.qty_to_invoice);
    if (Number.isFinite(direct)) return Math.max(direct, 0);
    const qtyOrdered = Number(it.qty_ordered) || 0;
    const qtyInvoiced = Number(it.qty_invoiced) || 0;
    const qtyRefunded = Number(it.qty_refunded) || 0;
    const qtyCanceled = Number(it.qty_canceled) || 0;
    return Math.max(qtyOrdered - qtyInvoiced - qtyRefunded - qtyCanceled, 0);
  };

  const qtyToShip = (it) => {
    const direct = Number(it.qty_to_ship);
    if (Number.isFinite(direct)) return Math.max(direct, 0);
    const qtyOrdered = Number(it.qty_ordered) || 0;
    const qtyShipped = Number(it.qty_shipped) || 0;
    const qtyCanceled = Number(it.qty_canceled) || 0;
    const qtyRefunded = Number(it.qty_refunded) || 0;
    return Math.max(qtyOrdered - qtyShipped - qtyCanceled - qtyRefunded, 0);
  };

  const editedItems = useMemo(() => {
    if (!order) return [];
    return (order.items || []).filter((it) => {
      const pendingQty = Math.max(qtyToInvoice(it), qtyToShip(it));
      return isFoundForItem(it) && removedQtyForItem(it, pendingQty) > 0;
    });
  }, [order, pickedMap, editedQty]);

  const formatQty = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0';
    if (Number.isInteger(num)) return String(num);
    return num.toFixed(2).replace(/\.?0+$/, '');
  };

  const getFulfillmentFlags = (it) => {
    const qtyOrdered = Number(it?.qty_ordered) || 0;
    const qtyCanceled = Number(it?.qty_canceled) || 0;
    const qtyRefunded = Number(it?.qty_refunded) || 0;
    const qtyInvoiced = Number(it?.qty_invoiced) || 0;
    const qtyShipped = Number(it?.qty_shipped) || 0;

    const baseTarget = Math.max(qtyOrdered - qtyCanceled - qtyRefunded, 0);
    const targetQty = Math.max(baseTarget, qtyInvoiced, qtyShipped);

    if (qtyInvoiced <= 0 && qtyShipped <= 0) return [];

    const invoiceState = qtyInvoiced >= targetQty ? 'complete' : qtyInvoiced > 0 ? 'partial' : 'pending';
    const shipState = qtyShipped >= targetQty ? 'complete' : qtyShipped > 0 ? 'partial' : 'pending';

    const flags = [];
    if (qtyInvoiced > 0) {
      flags.push({
        key: 'invoice',
        label: `Invoiced ${formatQty(qtyInvoiced)}`,
        state: invoiceState,
      });
    }
    if (qtyShipped > 0) {
      flags.push({
        key: 'shipment',
        label: `Shipped ${formatQty(qtyShipped)}`,
        state: shipState,
      });
    }

    return flags;
  };

  const renderFulfillmentFlags = (it) => {
    const flags = getFulfillmentFlags(it);
    if (!flags.length) return null;
    return (
      <div className="item-flag-row">
        {flags.map((flag) => (
          <span
            key={flag.key}
            className={`item-flag item-flag--${flag.key} state-${flag.state}`}
          >
            {flag.label}
          </span>
        ))}
      </div>
    );
  };

  const buildInvoiceItems = (ord) => {
    const payload = (ord.items || [])
      .filter((i) => qtyToInvoice(i) > 0)
      .filter((i) => isFoundForItem(i))
      .map((i) => {
        const maxInv = qtyToInvoice(i);
        const finalQty = editedQtyForItem(i, maxInv);
        return { order_item_id: i.item_id, qty: finalQty };
      })
      .filter((row) => row.qty > 0);

    console.debug('FinalizePage invoice payload:', payload);
    return payload;
  };

  const buildShipmentItems = (ord) => {
    const payload = (ord.items || [])
      .map((i) => {
        const type = typeof i?.product_type === 'string' ? i.product_type.toLowerCase() : '';
        const parentId = i.parent_item_id;
        if (type === 'bundle' || type === 'configurable' || type === 'grouped') {
          return null; // nunca shippar pais de bundle/configurable/grouped
        }
        const isChild = parentId !== undefined && parentId !== null;
        const allowedStandalone = new Set(['simple', 'virtual', 'downloadable']);
        const shippable = isChild || (allowedStandalone.has(type) && !parentId);
        if (!shippable) return null; // ignore bundle/configurable/grouped parents

        const maxShip = qtyToShip(i);
        if (maxShip <= 0) return null;
        if (!isFoundForItem(i)) return null;

        const finalQty = editedQtyForItem(i, maxShip);
        const qty = Math.min(Math.max(finalQty, 0), maxShip);
        if (qty <= 0) return null;

        return { order_item_id: i.item_id, qty };
      })
      .filter(Boolean);

    console.debug('FinalizePage shipment payload:', payload);
    return payload;
  };

  const buildItemsMapForResolvedFound = (ord) => {
    const list = buildInvoiceItems(ord);
    console.debug('FinalizePage itemsMap (from payload):', list);
    return list.reduce((acc, it) => {
      acc[String(it.order_item_id)] = Number(it.qty);
      return acc;
    }, {});
  };

  const buildItemActions = (ord) => {
    const items = Array.isArray(ord?.items) ? ord.items : [];
    const result = {
      remove: [],
      update: [],
    };

    items.forEach((item) => {
      const itemId = item?.item_id;
      if (itemId === undefined || itemId === null) return;

      const type = typeof item?.product_type === 'string' ? item.product_type.toLowerCase() : '';
      if (type === 'configurable') return; // configurable parents reference children that hold actual inventory

      const maxPending = qtyToInvoice(item);
      if (maxPending <= 0) return;

      const keepQty = editedQtyForItem(item, maxPending);
      const flaggedRemoved = isRemovedForItem(item);
      if (flaggedRemoved || keepQty <= 0) {
        result.remove.push({ item, qty: maxPending });
        return;
      }

      const trimmedKeep = Math.min(Math.max(keepQty, 0), maxPending);
      if (trimmedKeep !== maxPending && isFoundForItem(item)) {
        result.update.push({ item, qty: Number(trimmedKeep) });
      }
    });

    return result;
  };

  const parseResponsePayload = async (res) => {
    try {
      const text = await res.text();
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (jsonErr) {
        console.warn('FinalizePage: Non-JSON response body', jsonErr, text);
        return text;
      }
    } catch (err) {
      console.warn('FinalizePage: Failed to read response body', err);
      return null;
    }
  };

  const ensureMagentoSuccess = (res, body, fallbackMessage) => {
    if (!res.ok) {
      const message =
        (body && typeof body === 'object' && body.message) ||
        (typeof body === 'string' && body) ||
        fallbackMessage ||
        `Request failed (${res.status})`;
      throw new Error(message);
    }

    if (body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'success')) {
      if (!body.success) {
        const message = body.message || fallbackMessage || 'Request reported failure';
        throw new Error(message);
      }
    }

    return body;
  };

  const postOrderComment = async ({ comment, notify = 0, visible = 0 }) => {
    if (!order || !comment) return;
    const res = await fetch(buildRestUrl(`orders/${encodeURIComponent(order.entity_id)}/comments`), {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({
        statusHistory: {
          comment,
          is_customer_notified: notify ? 1 : 0,
          is_visible_on_front: visible ? 1 : 0,
        },
      }),
    });
    if (!res.ok) {
      const body = await res.text();
      throw new Error(body || 'Failed to post order comment');
    }
  };

  const notifyCustomerOfRemovals = async (removedEntries) => {
    if (!order || !removedEntries.length) return;
    const lines = removedEntries
      .map(({ item, qty }) => {
        const label = item?.name || item?.sku || `Item #${item?.item_id ?? ''}`;
        return `- ${formatQty(qty)}x ${label}`;
      })
      .join('\n');
    if (!lines) return;

    const message = [
      'Dear Customer,',
      "We're sorry to inform you that the following item(s) are out of stock and have been removed from your order:",
      '',
      lines,
      '',
      'Please note that when an order is placed, we only hold the payment. You are only charged for the items that are in stock and included in your final shipment.',
      'Please check your bank account in a few days to see the final charged amount.',
      'Apologies for the inconvenience caused.',
      'If you have any questions, feel free to contact us via WhatsApp at 0857476036.',
      'Thank you,',
      'Asia Market Team',
    ].join('\n');

    await postOrderComment({ comment: message, notify: 1 });
    console.debug('FinalizePage removal notification posted', { orderId: order.entity_id, lines });
  };

  const applyChanges = async () => {
    if (!order) return;
    setApplyBusy(true);
    setApplyMsg('');
    setInvoiceMsg('');
    try {
      const { remove: itemsToRemove, update: itemsToUpdate } = buildItemActions(order);

      let removedCount = 0;
      let updatedCount = 0;
      const deleteHeaders = { ...getAuthHeaders(), Accept: 'application/json' };
      const jsonHeaders = { ...getAuthHeaders(true), Accept: 'application/json' };

      const removedEntriesForEmail = [];

      for (const { item, qty } of itemsToRemove) {
        const url = buildRestUrl(`orders/${encodeURIComponent(order.entity_id)}/items/${encodeURIComponent(item.item_id)}/remove`);
        const res = await fetch(url, {
          method: 'DELETE',
          headers: deleteHeaders,
        });
        const payload = await parseResponsePayload(res);
        const label = item.sku || item.name || `#${item.item_id}`;
        ensureMagentoSuccess(res, payload, `Failed to remove item ${label}`);
        console.debug('FinalizePage remove response', { itemId: item.item_id, payload });
        removedCount += 1;
        removedEntriesForEmail.push({ item, qty });
      }

      for (const { item, qty } of itemsToUpdate) {
        const url = buildRestUrl(`orders/${encodeURIComponent(order.entity_id)}/items/${encodeURIComponent(item.item_id)}`);
        const numericQty = Number(qty);
        const qtyValue = Number.isFinite(numericQty) ? numericQty : 0;
        const res = await fetch(url, {
          method: 'PUT',
          headers: jsonHeaders,
          body: JSON.stringify({ itemData: { qty: qtyValue } }),
        });
        const payload = await parseResponsePayload(res);
        const label = item.sku || item.name || `#${item.item_id}`;
        ensureMagentoSuccess(res, payload, `Failed to update quantity for item ${label}`);
        console.debug('FinalizePage update response', { itemId: item.item_id, qty: qtyValue, payload });
        updatedCount += 1;
      }

      if (removedEntriesForEmail.length) {
        await notifyCustomerOfRemovals(removedEntriesForEmail);
      }

      const summaryBits = [];
      if (removedCount) summaryBits.push(`${removedCount} removed`);
      if (updatedCount) summaryBits.push(`${updatedCount} updated`);
      const summary = summaryBits.length ? ` (${summaryBits.join(', ')})` : '';

      setApplyMsg(`Changes applied successfully.${summary}`);
      setHasApplied(true);
      clearEditedEntriesForOrder();
      setForceCompleteReady(false);
      setForceCompleteMsg('');
      await reloadOrder();
    } catch (e) {
      console.error('Apply changes failed', e);
      const rawMessage = e.message || 'Failed to apply changes';
      setApplyMsg(rawMessage);
      alert(rawMessage);
      setHasApplied(false);
    } finally {
      setApplyBusy(false);
    }
  };

  const handleInvoice = async () => {
    if (!order || !hasApplied) return;
    const invoiceItems = buildInvoiceItems(order);
    if (!invoiceItems.length) {
      alert('No shippable items to invoice.');
      return;
    }

    setInvoiceBusy(true);
    setInvoiceMsg('');
    try {
      const payload = {
        capture: true,
        notify: false,
        appendComment: false,
        comment: {
          comment: 'Invoice created via picking app',
          is_visible_on_front: 0,
        },
        items: invoiceItems,
      };

      const res = await fetch(buildRestUrl(`order/${order.entity_id}/invoice`), {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `Invoice creation failed (${res.status})`);
      }

      setInvoiceMsg('Invoice created and payment captured.');
      await reloadOrder();
    } catch (e) {
      console.error(e);
      const msg = e.message || 'Failed to create invoice';
      setInvoiceMsg(msg);
      alert(msg);
    } finally {
      setInvoiceBusy(false);
    }
  };

  const createInvoiceBeforeShipping = async () => {
    const invoiceItems = buildInvoiceItems(order);
    if (!invoiceItems.length) {
      setInvoiceMsg('No invoiceable items.');
      return;
    }

    try {
      const payload = {
        capture: true,
        notify: false,
        appendComment: false,
        comment: {
          comment: 'Invoice created via picking app (auto before shipment)',
          is_visible_on_front: 0,
        },
        items: invoiceItems,
      };

      const res = await fetch(buildRestUrl(`order/${order.entity_id}/invoice`), {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `Invoice creation failed (${res.status})`);
      }

      setInvoiceMsg('Invoice created automatically before shipment.');
    } catch (err) {
      console.error('Auto-invoice before shipment failed', err);
      const msg = err.message || 'Failed to create invoice before shipment';
      setInvoiceMsg(msg);
      alert(msg);
    }
  };

  const handleShipmentOrPickup = async () => {
    if (!order || !hasApplied) return;
    setShipmentBusy(true);
    setShipmentMsg('');
    setForceCompleteReady(false);
    setForceCompleteMsg('');

    let attemptedShipment = false;

    try {
      const shippingDesc = (order.shipping_description || '').toLowerCase();
      const isClickCollect = shippingDesc.includes('click') || shippingDesc.includes('collect');

      if (isClickCollect) {
        await postOrderComment({
          comment: `Order marked as ready for pickup by ${operatorLabel} via picking app`,
          notify: 0,
        });
        await reloadOrder();
        setShipmentMsg('Marked as Ready for Pickup.');
        return;
      }

      attemptedShipment = true;

      // Sempre tentar criar invoice antes de shippar, mesmo que o shipment falhe depois
      await createInvoiceBeforeShipping();

      const shipmentItems = buildShipmentItems(order);
      if (!shipmentItems.length) {
        throw new Error('No shippable items remaining. Confirm the order still has items to ship.');
      }

      const shipmentComment = `Shipment created via picking app by ${operatorLabel}`;
      const payload = {
        items: shipmentItems,
        notify: false,
        appendComment: true,
        comment: {
          comment: shipmentComment,
          is_visible_on_front: 0,
        },
      };

      const res = await fetch(buildRestUrl(`order/${order.entity_id}/ship`), {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `Shipment creation failed (${res.status})`);
      }

      setShipmentMsg('Shipment created successfully.');
      setForceCompleteReady(false);
      setForceCompleteMsg('');
      await postOrderComment({
        comment: `Shipment created via picking app by ${operatorLabel}`,
        notify: 0,
      });
      await reloadOrder();
    } catch (e) {
      console.error(e);
      const msg = e.message || 'Failed to complete shipping action';
      setShipmentMsg(msg);
      alert(msg);
      if (attemptedShipment) {
        setForceCompleteReady(true);
      }
    } finally {
      setShipmentBusy(false);
    }
  };

  const handleForceComplete = async () => {
    if (!order) return;
    const confirmMessage = 'This will mark the order as Complete in Magento without creating a shipment. Continue?';
    if (!window.confirm(confirmMessage)) return;

    setForceCompleteBusy(true);
    setForceCompleteMsg('');
    try {
      const payload = {
        entity: {
          entity_id: Number(order.entity_id) || order.entity_id,
          state: 'complete',
          status: 'complete',
        },
      };

      const res = await fetch(buildRestUrl('orders'), {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `Failed to mark order ${order.increment_id} as complete`);
      }

      setForceCompleteReady(false);
      setForceCompleteMsg('Order marked as Complete without shipment.');
      setShipmentMsg('Order moved to Complete manually.');
      await postOrderComment({
        comment: `Order marked Complete without shipment by ${operatorLabel}`,
        notify: 0,
      });
      await reloadOrder();
    } catch (e) {
      console.error('Force-complete failed', e);
      const msg = e.message || 'Failed to mark order complete';
      setForceCompleteMsg(msg);
      alert(msg);
    } finally {
      setForceCompleteBusy(false);
    }
  };

  if (!isAuthed()) {
    return (
      <div className="finalize-page">
        <div className="finalize-shell">
          <div className="finalize-alert">You are not logged in.</div>
        </div>
      </div>
    );
  }

  const isSuccessMessage = applyMsg && applyMsg.toLowerCase().includes('success');
  const orderDate = order ? formatDateTime(order.created_at) : '';
  const totalItems = order?.items?.length || 0;
  const shippingDesc = (order?.shipping_description || '').toLowerCase();
  const isClickCollect = shippingDesc.includes('click') || shippingDesc.includes('collect');
  const isShippingCarrier = shippingDesc.includes('dpd') || shippingDesc.includes('dublin');
  const actionPanel = (
    <div className="finalize-actions">
      <button
        className="multi-picking-button action-button"
        onClick={applyChanges}
        disabled={applyBusy || loading}
      >
        {applyBusy ? 'Applying…' : 'Apply changes'}
      </button>
      {applyMsg && (
        <span
          className={`finalize-message ${isSuccessMessage ? 'finalize-message--success' : 'finalize-message--error'}`}
        >
          {applyMsg}
        </span>
      )}
      <button
        className="invoice-button action-button"
        onClick={handleInvoice}
        disabled={!hasApplied || invoiceBusy || loading}
      >
        {invoiceBusy ? 'Invoicing…' : 'Invoice'}
      </button>
      {invoiceMsg && (
        <span
          className={`finalize-message ${invoiceMsg.toLowerCase().includes('fail') ? 'finalize-message--error' : 'finalize-message--success'}`}
        >
          {invoiceMsg}
        </span>
      )}
      {(isShippingCarrier || isClickCollect) && (
        <>
          <button
            className={`shipping-button action-button ${isClickCollect ? 'ready' : ''}`}
            onClick={handleShipmentOrPickup}
            disabled={!hasApplied || shipmentBusy || loading}
          >
            {shipmentBusy
              ? isClickCollect ? 'Updating…' : 'Shipping…'
              : isClickCollect ? 'Ready for Pickup' : 'Create Shipment'}
          </button>
          {shipmentMsg && (
            <span
              className={`finalize-message ${shipmentMsg.toLowerCase().includes('fail') ? 'finalize-message--error' : 'finalize-message--success'}`}
            >
              {shipmentMsg}
            </span>
          )}
          {forceCompleteReady && (
            <div className="force-complete-panel">
              <p className="force-complete-hint">
                Shipping cannot be created (see error above). If the goods already left, mark the order as Complete without a shipment.
              </p>
              <button
                className="multi-picking-button action-button"
                onClick={handleForceComplete}
                disabled={forceCompleteBusy || loading}
              >
                {forceCompleteBusy ? 'Completing…' : 'Mark Complete (no shipment)'}
              </button>
            </div>
          )}
          {forceCompleteMsg && (
            <span
              className={`finalize-message ${forceCompleteMsg.toLowerCase().includes('fail') ? 'finalize-message--error' : 'finalize-message--success'}`}
            >
              {forceCompleteMsg}
            </span>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="finalize-page">
      <div className="finalize-shell">
        <div className="finalize-top-bar">
          <button className="finalize-back" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div className="finalize-title">
            <h1>Finalize Order {order ? `#${order.increment_id}` : ''}</h1>
            {order && (
              <div className="finalize-meta">
                <span><strong>Placed</strong> {orderDate}</span>
                <span><strong>Status</strong> <span className="status-pill">{order.status}</span></span>
                <span><strong>Customer</strong> {order.customer_firstname} {order.customer_lastname}</span>
                <span><strong>Items</strong> {totalItems}</span>
              </div>
            )}
          </div>
        </div>

        <LoadingOverlay show={loading} ariaLabel="Loading order" />

        {error && !loading && (
          <div className="finalize-message finalize-message--error">Error: {error}</div>
        )}

        {!loading && !error && order && (
          <div className="finalize-card">
            <div className={`finalize-status-banner ${allResolved ? 'is-ready' : 'is-pending'}`}>
              {allResolved
                ? 'All picking decisions are resolved. You can review the final quantities before invoicing.'
                : `${pendingCount} item${pendingCount === 1 ? '' : 's'} still need a Found or Remove decision before invoicing.`}
            </div>

            {actionPanel}

            <div className="finalize-sections">
              <section className="items-card found">
                <header>
                  <span>Found items</span>
                  <span className="items-badge">{foundItems.length}</span>
                </header>
                {foundItems.length === 0 ? (
                  <div className="item-empty">No items marked as found yet.</div>
                ) : (
                  <ul className="items-list">
                    {foundItems.map((it) => {
                      const pendingQty = Math.max(qtyToInvoice(it), qtyToShip(it));
                      const finalQty = editedQtyForItem(it, pendingQty);
                      const editedDiff = removedQtyForItem(it, pendingQty);
                      const rowClasses = ['item-row', 'item-row--found'];
                      if (editedDiff > 0) rowClasses.push('item-row--edited');
                      return (
                        <li key={it.item_id} className={rowClasses.join(' ')}>
                        <div className="item-info">
                          <span className="item-name">{it.name}</span>
                          {it.sku && <span className="item-sku">SKU {it.sku}</span>}
                          {renderFulfillmentFlags(it)}
                          {editedDiff > 0 && (
                            <span className="item-flag item-flag--edited">Qty adjusted {formatQty(pendingQty)} → {formatQty(finalQty)}</span>
                          )}
                        </div>
                        <span className="item-qty">{finalQty}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section className="items-card edited">
                <header>
                  <span>Edited items</span>
                  <span className="items-badge">{editedItems.length}</span>
                </header>
                {editedItems.length === 0 ? (
                  <div className="item-empty">No quantity edits for this order.</div>
                ) : (
                  <ul className="items-list">
                    {editedItems.map((it) => {
                      const pendingQty = Math.max(qtyToInvoice(it), qtyToShip(it));
                      const finalQty = editedQtyForItem(it, pendingQty);
                      const removedQty = removedQtyForItem(it, pendingQty);
                      return (
                        <li key={it.item_id} className="item-row item-row--edited">
                          <div className="item-info">
                            <span className="item-name">{it.name}</span>
                            {it.sku && <span className="item-sku">SKU {it.sku}</span>}
                            {renderFulfillmentFlags(it)}
                            <span className="item-flag item-flag--edited">
                              Qty adjusted {formatQty(pendingQty)} → {formatQty(finalQty)}
                            </span>
                            <span className="item-sku">Removed {formatQty(removedQty)}</span>
                          </div>
                          <span className="item-qty">{formatQty(finalQty)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section className="items-card removed">
                <header>
                  <span>Removed items</span>
                  <span className="items-badge">{removedItems.length}</span>
                </header>
                {removedItems.length === 0 ? (
                  <div className="item-empty">Nothing was removed from this order.</div>
                ) : (
                  <ul className="items-list">
                    {removedItems.map((it) => (
                      <li key={it.item_id} className="item-row item-row--removed">
                        <div className="item-info">
                          <span className="item-name">{it.name}</span>
                          {it.sku && <span className="item-sku">SKU {it.sku}</span>}
                          {renderFulfillmentFlags(it)}
                        </div>
                        <span className="item-qty item-qty--danger">{qtyToInvoice(it)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default FinalizePage;
